import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize, map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { MOCK_ROUTES } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExceptionsReportsService } from 'src/app/core/services/reports/exceptions/exceptions-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import {
  CONSTS,
  ExceptionProcessingFormGroup,
  REPORT_FILE_NAME,
} from './exception-processing-report.constants';

@Component({
  selector: 'app-exception-processing-report',
  templateUrl: './exception-processing-report.component.html',
  styleUrls: ['./exception-processing-report.component.scss'],
  standalone: false,
})
export class ExceptionProcessingReportComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: ExceptionProcessingFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<ExceptionProcessingReportComponent> = inject(
    MatDialogRef<ExceptionProcessingReportComponent>,
  );
  private commonUtils: CommonUtils = inject(CommonUtils);
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  private exceptionsReportsService: ExceptionsReportsService = inject(
    ExceptionsReportsService,
  );

  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  exceptionProcessingForm!: FormGroup;
  currentLocation!: string;

  routeIds = MOCK_ROUTES;

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.currDate = new Date();
          this.minDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() - 30,
          );
          this.maxDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() + 14,
          );

          this.exceptionProcessingForm = this.fb.group({
            date: [this.currDate],
            routeIds: [MOCK_ROUTES[0]],
          });

          this.currentLocation = user?.location;
          return this.getRouteList(this.currentLocation);
        }),
      )
      .subscribe();
  }

  onExit() {
    this.commonUtils.openExitModal(this.exceptionProcessingForm.dirty);
  }

  /**
   * Used to get route list
   *
   * @private
   * @memberof ExceptionProcessingReportComponent
   */
  private getRouteList(code: string) {
    const request = {
      locationcode: code,
    };
    return this.exceptionsReportsService
      .getExceptionProcessingRoutes(request)
      .pipe(
        take(1),
        map((data) => {
          this.routeIds = data.response.excepProcRoutes
            .map(({ route }) => {
              return {
                label: route,
                value: route,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          this.routeIds.unshift({
            label: STATUS_OPTIONS.ALL_SELECTED,
            value: 'All',
          });
        }),
      );
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof ExceptionProcessingReportComponent
   */
  getReport() {
    const formValue = this.exceptionProcessingForm.value;

    const date = this.commonUtils.getFormattedDate(formValue.date);
    const request = {
      locationcode: this.currentLocation,
      date,
      route: formValue.routeIds?.value === 'All' ? 'All' : formValue.routeIds,
    };
    this.exceptionsReportsService
      .getExceptionProcessingReport(request)
      .pipe(
        take(1),
        map((data) => {
          const exceptionReport = data.response.exceptionReport;

          if (!exceptionReport?.length) {
            return this.commonUtils.updateBannerType(
              BANNER_TYPE.NO_DATA_AVAILABLE,
            );
          }

          const customHeader = [
            ['', '', '', '', `${'Exception Processing Export'}`, '', '', ''],
          ];

          return this.reportUtils.exportCSVReport(
            exceptionReport,
            REPORT_FILE_NAME,
            customHeader,
          );
        }),
        finalize(() => {
          this.dialogRef.close();
        }),
      )
      .subscribe();
  }
}
